import { useRouter } from "next/router";
import {
  InfoCircleOutlined,
  LinkOutlined,
  StopOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  PlusOutlined,
  MinusOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import { theme, Switch, Divider, Tooltip, Menu } from "antd";
import { PropsWithChildren, ReactNode, useState } from "react";
import { CopyIcon } from "src/components/CopyIcon";
import { GroupByIcon } from "src/components/GroupByIcon";
import { Hover } from "src/components/Hover";
import { usePopupContext } from "src/hooks/usePopup";
import { Field, SearchActions } from "src/search/types";
import { SearchContext } from "src/search/useSearchContext";
import { asKey } from "src/utils/fields";
import { attributeToTitle } from "src/utils/strings";
import { ValueFormatter } from "src/utils/formatters";
import {
  ensureState,
  serializeParams,
} from "src/search/lib/params-state-reducer";
import { serializeContentView } from "pages/[orgId]/explore/lib/useContentView";
import { serializeDocument } from "pages/[orgId]/explore/lib/useExploreDocument";
import { serializeTabs } from "pages/[orgId]/explore/lib/useTabs";
import { ContentViews } from "pages/[orgId]/explore/lib/useContentView";
import { valueToLabel } from "pages/[orgId]/explore/lib/common";

export interface FilterMenuOptions {
  showFilters?: boolean; // Show filter options
  showGroupBy?: boolean; // Show group by options
  showPivot?: boolean; // Show pivot options
  showSort?: boolean; // Show sort options
  showAggregate?: boolean; // Show aggregate options
  showDrillDown?: boolean; // Show drill down options
  showEntityOpener?: boolean; // Show entity opener options
  showValueCopiers?: boolean; // Show value copiers options
}

// Default all options to true
const DEFAULT_FILTER_MENU_OPTIONS: Required<FilterMenuOptions> = {
  showFilters: true,
  showGroupBy: true,
  showPivot: true,
  showSort: true,
  showAggregate: true,
  showDrillDown: true,
  showEntityOpener: true,
  showValueCopiers: true,
};

// Add utility function to normalize values
function normalizeFieldValue(value: any): { label: string; value: string } {
  if (value && typeof value === "object") {
    const actualValue = value.value || "";
    return {
      label: String(actualValue),
      value: String(actualValue),
    };
  }
  return {
    label: String(value || ""),
    value: String(value || ""),
  };
}

// Add validation utilities
function isValidValue(
  value: { label: string; value: string } | undefined
): boolean {
  return (
    !!value &&
    value.value !== "__VZBL::null__" &&
    value.value !== undefined &&
    value.value !== null &&
    value.value !== ""
  );
}

function isValidField(property: Field | undefined): boolean {
  return !!property && !!property.attribute && property.attribute !== "";
}

function isValidSearchItem(
  property: Field,
  value: { label: string; value: string }
): boolean {
  // Skip count aggregations
  if (property.attribute === "count" || asKey(property).includes(".count(")) {
    return false;
  }
  return isValidField(property) && isValidValue(value);
}

function FilterItem({
  property,
  value,
  params,
  popup,
  formatters,
  index,
  canFilter = true,
}: {
  property: Field;
  value: { label: string; value: string };
  params: SearchContext["params"];
  popup: ReturnType<typeof usePopupContext>;
  formatters?: ((val: string) => string)[];
  index: number;
  canFilter?: boolean;
}) {
  // Skip rendering if invalid
  if (!isValidSearchItem(property, value)) {
    return null;
  }

  const { token } = theme.useToken();
  const isActive = params.selectors.hasAttributeFilterValue(
    params.state,
    property,
    value.value
  );

  // Format the value using ValueFormatter
  const formattedValue = (() => {
    // First try using the provided formatter if available
    if (formatters?.at(index)) {
      return formatters[index](value.label);
    }

    // Otherwise use ValueFormatter
    const fieldName = asKey(property);
    const detectedType = ValueFormatter.detectValueType({ field: fieldName });

    if (!detectedType) {
      return value.label; // Return original value if no formatter is applicable
    }

    // Handle non-numeric special cases
    if (value.value === "__VZBL::null__") {
      return "n/a";
    }

    // Handle comparison values - extract the second value
    const compareMatch = value.value.toString().match(/^.*?,(.*)$/);
    if (compareMatch) {
      const secondValue = compareMatch[1];
      const numValue = Number(secondValue);
      if (!Number.isNaN(numValue)) {
        return ValueFormatter.format(numValue, {
          type: detectedType,
          field: fieldName,
          style: "human",
        });
      }
      return secondValue;
    }

    // Convert to number for numeric formatters if possible
    const numValue = Number(value.value);
    if (!Number.isNaN(numValue)) {
      return ValueFormatter.format(numValue, {
        type: detectedType,
        field: fieldName,
        style: "human",
      });
    }

    return value.label;
  })();

  const propertyName = attributeToTitle(asKey(property));

  return (
    <Hover
      baseStyle={{
        backgroundColor: "transparent",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "4px 12px",
        borderRadius: 6,
        margin: "2px 6px",
        lineHeight: "1.2",
        overflow: "hidden",
      }}
      hoverStyle={{}}
    >
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div
          style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <span
              style={{
                fontSize: "11px",
                fontWeight: 500,
                color: token.colorTextTertiary,
                textTransform: "uppercase",
                letterSpacing: "0.3px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontVariant: "small-caps",
              }}
            >
              {propertyName}
            </span>
            <span
              style={{
                fontSize: "13px",
                fontWeight: 500,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: "1.5",
                color: token.colorText,
              }}
            >
              {formattedValue}
            </span>
          </div>
        </div>
        {canFilter && (
          <div
            style={{ display: "flex", gap: 4, flexShrink: 0, marginLeft: 8 }}
          >
            <Tooltip title="Add filter to include">
              <div>
                <Hover
                  baseStyle={{
                    padding: "4px",
                    cursor: "pointer",
                    borderRadius: 4,
                    fontSize: "11px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 20,
                    height: 20,
                    transition: "all 0.2s ease",
                  }}
                  hoverStyle={{
                    backgroundColor: token.colorBgTextHover,
                  }}
                >
                  <div
                    onClick={() => {
                      popup.close();
                      params.dispatch({
                        type: SearchActions.setFilters,
                        value: params.modifiers.setAndAddFilters({
                          state: params.state,
                          operator: "IN",
                          property,
                          value: value.value,
                        }),
                      });
                    }}
                  >
                    <PlusOutlined style={{ fontSize: 11 }} />
                  </div>
                </Hover>
              </div>
            </Tooltip>
            <Tooltip title="Add filter to exclude">
              <div>
                <Hover
                  baseStyle={{
                    padding: "4px",
                    cursor: "pointer",
                    borderRadius: 4,
                    fontSize: "11px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 20,
                    height: 20,
                    transition: "all 0.2s ease",
                  }}
                  hoverStyle={{
                    backgroundColor: token.colorBgTextHover,
                  }}
                >
                  <div
                    onClick={() => {
                      popup.close();
                      params.dispatch({
                        type: SearchActions.setFilters,
                        value: params.modifiers.setAndAddFilters({
                          state: params.state,
                          operator: "NOT IN",
                          property,
                          value: value.value,
                        }),
                      });
                    }}
                  >
                    <MinusOutlined style={{ fontSize: 11 }} />
                  </div>
                </Hover>
              </div>
            </Tooltip>
          </div>
        )}
      </div>
    </Hover>
  );
}

export interface FilterMenuProps {
  context: SearchContext;
  properties: Field[];
  values: { value: string; label: string }[];
  entities?: { uuid: string; label?: string }[];
  open?: boolean;
  measureValue?: { value: string; label: string };
  formatters?: ((val: string) => string)[];
  options?: FilterMenuOptions;
  accentColor?: string;
}

export const FilterMenu = (props: FilterMenuProps) => {
  const { token } = theme.useToken();
  const { copiers, ...actionItems } = useFilterMenuItems(props);
  const popup = usePopupContext();
  const options = { ...DEFAULT_FILTER_MENU_OPTIONS, ...props.options }; // Merge defaults with provided options

  return (
    <div
      style={{
        padding: "8px 4px",
        width: 280,
        maxHeight: "70vh",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {props.accentColor && (
        <div
          style={{
            border: `solid 1px ${token.colorBgLayout}`,
            backgroundColor: props.accentColor || token.colorBgBase,
            height: 6,
            borderTopLeftRadius: token.borderRadiusLG,
            borderTopRightRadius: token.borderRadiusLG,
          }}
        />
      )}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {/* Filter Items */}
        {options.showFilters && (
          <div>
            {props.properties.map((property, i) => (
              <FilterItem
                key={`${property.attribute}-${props.values[i].value}`}
                property={property}
                value={props.values[i]}
                params={props.context.params}
                popup={popup}
                formatters={props.formatters}
                index={i}
                canFilter={true}
              />
            ))}
            {props.measureValue && (
              <FilterItem
                key={`measure-${props.measureValue.label}`}
                property={{
                  target: "Event",
                  attribute: props.measureValue.label,
                }}
                value={{
                  label: props.measureValue.value,
                  value: props.measureValue.value,
                }}
                params={props.context.params}
                popup={popup}
                index={-1}
                canFilter={true}
              />
            )}
          </div>
        )}

        {/* Group By section */}
        {options.showGroupBy && actionItems.groupBys.length > 0 && (
          <>
            <Divider style={{ margin: "4px 0" }} />
            <div key="group2">
              {actionItems.groupBys.map((item: ActionItem, idx: number) => (
                <div key={idx}>
                  <ActionOption {...item} />
                </div>
              ))}
            </div>
          </>
        )}

        {/* Entity Opener section */}
        {options.showEntityOpener && actionItems.entityOpener.length > 0 && (
          <>
            <Divider style={{ margin: "8px 0" }} />
            <div key="group3">
              {actionItems.entityOpener.map((item: ActionItem, idx: number) => (
                <div key={idx}>
                  <ActionOption {...item} />
                </div>
              ))}
            </div>
          </>
        )}

        {/* Drill Down section */}
        {options.showDrillDown && actionItems.drillDown.length > 0 && (
          <>
            <Divider style={{ margin: "8px 0" }} />
            <div key="group4">
              {actionItems.drillDown.map((item: ActionItem, idx: number) => (
                <div key={idx}>
                  <ActionOption {...item} />
                </div>
              ))}
            </div>
          </>
        )}

        {/* Value Copiers section */}
        {options.showValueCopiers && copiers.length > 0 && (
          <>
            <Divider style={{ margin: "8px 0" }} />
            <div key="group5">
              {copiers.map((copier: CopierItem, idx: number) => (
                <div key={idx}>
                  <ActionOption
                    label={
                      <OptionLabel
                        icon={<CopyIcon />}
                        text="Copy"
                        value={copier.value}
                      />
                    }
                    onClick={copier.onClick}
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

function useFilterMenuItems(props: {
  context: SearchContext;
  properties: Field[];
  values: { label: string; value: string }[];
  entities?: { uuid: string; label?: string }[];
  open?: boolean;
  measureValue?: { label: string; value: string };
  formatters?: ((val: string) => string)[];
  options?: FilterMenuOptions;
}) {
  const {
    context,
    properties,
    values,
    entities,
    measureValue,
    formatters,
    options = {},
  } = props;
  const { params, entity } = context;

  // Always call hooks, but conditionally use their results
  const groupBys = useGroupBys({ params, properties });
  const filterOns = useFilterOns({ params, properties, values, formatters });
  const filterOffs = useFilterOffs({ params, properties, values, formatters });
  const filterLess = useFilterLess({ params, properties, values, formatters });
  const drillDown = useDrillDown({ params, properties, values });
  const entityOpener = useEntityOpeners({
    properties,
    values,
    entity,
    entities,
  });
  const copiers = useValueCopiers({
    properties,
    values,
    measureValue,
    formatters,
  });

  return {
    groupBys: options.showGroupBy ? groupBys : [],
    filterOns: options.showFilters ? filterOns : [],
    filterOffs: options.showFilters ? filterOffs : [],
    filterLess: options.showFilters ? filterLess : [],
    drillDown: options.showDrillDown ? drillDown : [],
    entityOpener: options.showEntityOpener ? entityOpener : [],
    copiers: options.showValueCopiers ? copiers : [],
  };
}

/* Creates a set of labels and actions for grouping on a field */
function useGroupBys({
  properties,
  params,
}: {
  properties: Field[];
  params: SearchContext["params"];
}) {
  const popup = usePopupContext();
  return properties
    .filter(
      (property) =>
        !params.selectors.hasGroupBy(params.state, property.attribute)
    )
    .map((property) => ({
      label: (
        <OptionLabel
          icon={<GroupByIcon />}
          text="Group by"
          value={attributeToTitle(asKey(property))}
        />
      ),
      onClick() {
        popup.close();
        params.dispatch({
          type: SearchActions.setGroupBys,
          value: [...(params.state.groupBys || []), property],
        });
        // TODO open in new vzbl tab
      },
    }));
}

/* Creates a set of labels and actions for including a value for a field */
function useFilterOns({
  properties,
  params,
  values,
  formatters,
}: {
  values: { label: string; value: string }[];
  properties: Field[];
  params: SearchContext["params"];
  formatters?: ((val: string) => string)[];
}) {
  const popup = usePopupContext();
  return properties
    .map(
      (property, i) =>
        [property, values[i]] as [Field, { label: string; value: string }]
    )
    .filter(
      ([property, value]) =>
        isValidSearchItem(property, value) &&
        !params.selectors.hasAttributeFilterValue(
          params.state,
          property,
          value.value
        )
    )
    .map(([property, value], i) => ({
      label: (
        <OptionLabel
          icon={<ZoomInOutlined />}
          text={`Only show`}
          value={formatters?.at(i) ? formatters[i](value.label) : value.label}
        />
      ),
      onClick() {
        popup.close();
        params.dispatch({
          type: SearchActions.setFilters,
          value: params.modifiers.setAndAddFilters({
            state: params.state,
            operator: "IN",
            property,
            value: value.value,
          }),
        });
      },
    }));
}

/* Creates a set of labels and actions for excluding a value for a field */
function useFilterOffs({
  properties,
  params,
  values,
  formatters,
}: {
  values: { label: string; value: string }[];
  properties: Field[];
  params: SearchContext["params"];
  formatters?: ((val: string) => string)[];
}) {
  const popup = usePopupContext();
  return properties
    .map(
      (property, i) =>
        [property, values[i]] as [Field, { label: string; value: string }]
    )
    .filter(
      ([property, value]) =>
        isValidSearchItem(property, value) &&
        !params.selectors.hasAttributeFilterOperator(
          params.state,
          property,
          "IN"
        ) &&
        !params.selectors.hasAttributeFilterValue(
          params.state,
          property,
          value.value
        )
    )
    .map(([property, value], i) => ({
      label: (
        <OptionLabel
          icon={<StopOutlined />}
          text={`Don't show`}
          value={formatters?.at(i) ? formatters[i](value.label) : value.label}
        />
      ),
      onClick() {
        popup.close();
        params.dispatch({
          type: SearchActions.setFilters,
          value: params.modifiers.setAndAddFilters({
            state: params.state,
            operator: "NOT IN",
            property,
            value: value.value,
          }),
        });
      },
    }));
}

/* Creates a set of labels and actions for removing a value for a field filter */
function useFilterLess({
  properties,
  params,
  values,
  formatters,
}: {
  values: { label: string; value: string }[];
  properties: Field[];
  params: SearchContext["params"];
  formatters?: ((val: string) => string)[];
}) {
  const popup = usePopupContext();
  return properties
    .map(
      (property, i) =>
        [property, values[i]] as [Field, { label: string; value: string }]
    )
    .filter(
      ([property, value]) =>
        isValidSearchItem(property, value) &&
        params.selectors.hasAttributeFilterValue(
          params.state,
          property,
          value.value
        )
    )
    .map(([property, value], i) => ({
      label: (
        <OptionLabel
          icon={<ZoomOutOutlined />}
          text={`Remove`}
          value={formatters?.at(i) ? formatters[i](value.label) : value.label}
        />
      ),
      onClick() {
        popup.close();
        params.dispatch({
          type: SearchActions.setFilters,
          value: params.modifiers.removeFilters({
            property,
            value: value.value,
            state: params.state,
          }),
        });
      },
    }));
}

interface ActionItem {
  label: JSX.Element;
  onClick(): void;
}

interface CopierItem {
  value: string;
  attribute: string;
  onClick(): void;
}

/* creates the option to drill down into a new result set */
function useDrillDown({
  properties,
  values,
  params,
}: {
  properties: Field[];
  values: { label: string; value: string }[];
  params: SearchContext["params"];
}): ActionItem[] {
  const router = useRouter();
  const orgId = `${router.query.orgId}`;
  const popup = usePopupContext();
  if (params.selectors.countGroupBys(params.state) === 0) return [];
  return [
    {
      label: (
        <OptionLabel
          icon={<ArrowRightOutlined />}
          text="Explore Results"
          value=""
        />
      ),
      onClick() {
        popup.close();
        const record: Record<string, any> = {};
        properties.forEach((gb, i) => {
          const value = values[i];
          const normalizedValue = normalizeFieldValue(value).value;
          record[asKey(gb)] = normalizedValue;
        });
        const state = params.modifiers.filterDegrouped({
          state: params.state,
          record,
          properties,
        });

        const title =
          Object.entries(record)
            .map(
              ([key, val]) => `${attributeToTitle(key)} is ${valueToLabel(val)}`
            )
            .join(", ") || `Metric events`;
        const path = `/${orgId}/explore`;
        const d = encodeURIComponent(
          serializeDocument({ saved: false, title })
        );
        const s = encodeURIComponent(
          serializeParams(ensureState(state, orgId))
        );
        const t = encodeURIComponent(serializeTabs({}));
        const v = encodeURIComponent(
          serializeContentView({
            value: ContentViews.default,
            config: {},
          })
        );
        const exploreUrl = `${path}?s=${s}&v=${v}&d=${d}&t=${t}`;
        router.push({
          pathname: "/[orgId]/t/new",
          query: { orgId, title, url: exploreUrl },
        });
      },
    },
  ];
}

/* opens given entities by UUID in the panel */
function useEntityOpeners({
  properties,
  values,
  entity,
  entities = [],
}: {
  entities?: { uuid: string; label?: string }[];
  values: { label: string; value: string }[];
  properties: Field[];
  entity: SearchContext["entity"];
}): ActionItem[] {
  const popup = usePopupContext();
  return entities.map(({ uuid, label }) => ({
    label: (
      <OptionLabel
        icon={<InfoCircleOutlined />}
        text="Show this"
        value={
          label ||
          (properties[0].attribute === "EntityType"
            ? attributeToTitle(values[0].label)
            : properties[0].attribute)
        }
      />
    ),
    onClick() {
      popup.close();
      entity.open({ uuid });
    },
  }));
}

/* creates options for copying the various values to the clipboard */
function useValueCopiers({
  properties,
  values,
  measureValue,
  formatters,
}: {
  properties: Field[];
  values: { label: string; value: string }[];
  measureValue?: { label: string; value: string };
  formatters?: ((str: string) => string)[];
}): CopierItem[] {
  const popup = usePopupContext();
  const copiers = properties.map((property, i) => {
    const format = formatters?.at(i) || ((s) => s);
    const value = format(values[i].value);
    return {
      value,
      attribute: property.attribute,
      onClick() {
        popup.close();
        navigator.clipboard?.writeText(value);
      },
    };
  });
  if (measureValue)
    copiers.push({
      value: measureValue.value,
      attribute: measureValue.label,
      onClick() {
        popup.close();
        navigator.clipboard?.writeText(measureValue.value);
      },
    });
  return copiers;
}

function ActionOption(props: ActionItem) {
  const { token } = theme.useToken();
  return (
    <Hover
      baseStyle={{
        backgroundColor: "transparent",
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        cursor: "pointer",
        color: token.colorText,
        padding: "4px 12px",
        borderRadius: 6,
        margin: "4px 6px",
        lineHeight: "1.2",
        overflow: "hidden",
      }}
      hoverStyle={{
        backgroundColor: token.colorBgTextHover,
      }}
    >
      <div
        onClick={props.onClick}
        onAuxClick={props.onClick}
        style={{
          width: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {props.label}
      </div>
    </Hover>
  );
}

function OptionLabel(props: { icon: ReactNode; text: string; value: string }) {
  return (
    <span
      style={{
        gap: 4,
        display: "inline-flex",
        justifyContent: "flex-start",
        alignItems: "center",
        fontSize: 14,
      }}
    >
      <span>{props.icon}</span>
      <span>{props.text}</span>
      <ValueLabel value={props.value} />
    </span>
  );
}

function ValueLabel(props: { value: any }) {
  if (props.value === "__VZBL::null__")
    return (
      <span style={{ display: "inline-block", fontWeight: 600, opacity: 0.6 }}>
        null
      </span>
    );
  return (
    <div
      style={{
        display: "inline-flex",
        height: "100%",
        alignItems: "center",
        minWidth: 0,
        maxWidth: 160,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      <span
        className="trunc"
        style={{
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 600,
          display: "inline-block",
          color: "inherit",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          width: "100%",
        }}
      >
        {props.value}
      </span>
    </div>
  );
}
